import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { companyApi } from '../../api/company.api';
import { Avatar, Badge, EmptyState, PageHeader, Skeleton, Btn, Input } from '../../components/ui/index';
import { toPersian } from '../../utils/persianNumber';
import { useDebounce } from '../../hooks/useDraft';
import { useAuth } from '../../store/AuthContext';
import { useUI } from '../../store/UIContext';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';
import { MagnifyingGlass, Prohibit, PencilSimple } from '@phosphor-icons/react';

const FILTERS = ['همه', 'فعال', 'غیرفعال'];
const COLORS = ['#29AAE1', '#8b5cf6', '#059669', '#f59e0b', '#ef4444', '#f97316', '#0ea5e9', '#6366f1'];
const CAN_VIEW = ['dispatcher', 'manager_employee', 'admin'];

export default function CompanyListPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToast } = useUI();
  const [searchParams] = useSearchParams();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('همه');
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', display_name: '', description: '' });
  const [savingEdit, setSavingEdit] = useState(false);
  const q = useDebounce(search, 300);
  const allowed = CAN_VIEW.includes(user?.type);
  const canDelete = user?.type === 'dispatcher' || user?.type === 'admin';

  const load = (showSpinner = true) => {
    if (!allowed) { setLoading(false); return Promise.resolve(); }
    if (showSpinner) setLoading(true);
    return companyApi.getCompanyListView()
      .then((r) => setCompanies(Array.isArray(r.data) ? r.data : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [allowed]);

  const openEdit = async (c) => {
    setEditTarget(c);
    setEditForm({
      name: c.name || '',
      display_name: c.display_name && c.display_name !== c.name ? c.display_name : '',
      description: '',
    });
    try {
      const res = await companyApi.getCompanyInfo(c.id);
      setEditForm((f) => ({ ...f, description: res.data?.description || '' }));
    } catch { /* description stays empty; save still works */ }
  };

  const saveEdit = async () => {
    if (!editForm.name.trim()) { addToast('نام شرکت الزامی است', 'error'); return; }
    setSavingEdit(true);
    try {
      await companyApi.editCompanyInfo({
        id: editTarget.id,
        name: editForm.name.trim(),
        display_name: editForm.display_name.trim(),
        description: editForm.description,
      });
      addToast('اطلاعات شرکت به‌روزرسانی شد', 'success');
      setEditTarget(null);
      await load(false);
    } catch (err) {
      const msg = err.response?.data;
      addToast(typeof msg === 'string' && msg ? msg : 'خطا در ویرایش شرکت', 'error');
    }
    setSavingEdit(false);
  };
  useAutoRefresh(() => load(false), { enabled: allowed, paused: !!deleteTarget });

  const filtered = companies.filter((c) => {
    const m = !q || (c.name || '').includes(q) || (c.display_name || '').includes(q);
    if (filter === 'فعال') return m && !!c.is_active;
    if (filter === 'غیرفعال') return m && !c.is_active;
    return m;
  });

  const toggleFlag = async (c, field) => {
    try {
      await companyApi.setCompanyContractStatus({ id: c.id, [field]: (field === 'is_active' ? !c.is_active : !c.has_contract) ? 1 : 0 });
      addToast('وضعیت شرکت به‌روزرسانی شد', 'success');
      await load(false);
    } catch (err) {
      const msg = err.response?.data;
      addToast(typeof msg === 'string' && msg ? msg : 'خطا در تغییر وضعیت', 'error');
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget?.id) return;
    setDeleting(true);
    try {
      await companyApi.deleteCompany({ company_id: deleteTarget.id });
      addToast('شرکت حذف شد', 'success');
      setDeleteTarget(null);
      await load(false);
    } catch (err) {
      const msg = err.response?.data;
      addToast(typeof msg === 'string' && msg ? msg : 'خطا در حذف شرکت', 'error');
    }
    setDeleting(false);
  };

  if (!allowed) {
    return (
      <div className="fade-in">
        <EmptyState icon={<Prohibit size={44} weight="regular" />} title="دسترسی غیرمجاز" subtitle="شما اجازه مشاهده لیست شرکت‌ها را ندارید" />
      </div>
    );
  }

  return (
    <div className="fade-in">
      <PageHeader
        title="لیست شرکت‌ها"
        subtitle={`${toPersian(filtered.length)} شرکت`}
        action={<Btn onClick={() => navigate('/companies/add')}>+ افزودن شرکت</Btn>}
      />
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {FILTERS.map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            type="button"
            style={{
              padding: '7px 16px', borderRadius: 20,
              border: `1.5px solid ${filter === f ? '#29AAE1' : '#e2e8f0'}`,
              background: filter === f ? '#EDF3FC' : '#fff',
              color: filter === f ? '#233A7A' : '#64748b',
              fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            {f}
          </button>
        ))}
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="جستجو..."
          style={{
            marginRight: 'auto', padding: '7px 14px', border: '1.5px solid #e2e8f0',
            borderRadius: 20, fontSize: 12, outline: 'none', fontFamily: 'inherit', width: 200,
          }}
        />
      </div>
      {loading ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: 14 }}>
          {[1, 2, 3, 4].map((k) => (
            <div key={k} style={{ background: '#fff', borderRadius: 14, padding: 20, border: '1px solid #e2e8f0' }}>
              <Skeleton h={20} style={{ marginBottom: 10 }} /><Skeleton h={14} w="70%" />
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState icon={<MagnifyingGlass size={44} weight="regular" />} title="شرکتی یافت نشد" />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: 14 }}>
          {filtered.map((c, i) => (
            <div
              key={c.id || i}
              className="pj-card-hover"
              role="button"
              tabIndex={0}
              onClick={() => navigate(`/companies/${c.id}`, { state: { company: c } })}
              onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/companies/${c.id}`, { state: { company: c } }); }}
              style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', padding: 20, cursor: 'pointer' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
                <Avatar name={(c.name || '').slice(0, 2)} size={46} color={COLORS[i % COLORS.length]} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {c.display_name || c.name}
                  </div>
                  {c.display_name && c.display_name !== c.name && <div style={{ fontSize: 11, color: '#94a3b8' }} dir="auto">پیشوند: {c.name}</div>}
                  {c.project_manager && <div style={{ fontSize: 11, color: '#94a3b8' }}>مدیر پروژه: {c.project_manager}</div>}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: canDelete ? 12 : 0 }}>
                <Badge variant={c.is_active ? 'green' : 'gray'}>{c.is_active ? 'فعال' : 'غیرفعال'}</Badge>
                <Badge variant={c.has_contract ? 'green' : 'amber'}>{c.has_contract ? 'دارای قرارداد' : 'بدون قرارداد'}</Badge>
                {c.contract_type && <Badge variant="blue">{c.contract_type}</Badge>}
              </div>
              {canDelete && (
                <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                  <Btn size="sm" variant="ghost" style={{ flex: 1, justifyContent: 'center' }}
                    onClick={(e) => { e.stopPropagation(); toggleFlag(c, 'is_active'); }}>
                    {c.is_active ? 'غیرفعال کردن' : 'فعال کردن'}
                  </Btn>
                  <Btn size="sm" variant="ghost" style={{ flex: 1, justifyContent: 'center' }}
                    onClick={(e) => { e.stopPropagation(); toggleFlag(c, 'has_contract'); }}>
                    {c.has_contract ? 'لغو قرارداد' : 'ثبت قرارداد'}
                  </Btn>
                </div>
              )}
              {canDelete && (
                <div style={{ display: 'flex', gap: 8 }}>
                  <Btn
                    size="sm"
                    variant="secondary"
                    style={{ flex: 1, justifyContent: 'center' }}
                    onClick={(e) => { e.stopPropagation(); openEdit(c); }}
                  >
                    <PencilSimple size={14} weight="bold" style={{ marginLeft: 6 }} />
                    ویرایش
                  </Btn>
                  <Btn
                    size="sm"
                    variant="danger"
                    style={{ flex: 1, justifyContent: 'center' }}
                    onClick={(e) => { e.stopPropagation(); setDeleteTarget(c); }}
                  >
                    حذف شرکت
                  </Btn>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {editTarget && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
        }}
        >
          <div style={{
            background: '#fff', borderRadius: 14, padding: 24, width: '100%', maxWidth: 420,
            maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
          }}
          >
            <h3 style={{ fontSize: 15, fontWeight: 700, margin: '0 0 8px', color: '#0f172a' }}>ویرایش شرکت</h3>
            <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 16px', lineHeight: 1.7 }}>
              نام شرکت پیشوند نام کاربری مشتریان است؛ نام نمایشی در بقیه‌ی بخش‌های برنامه نشان داده می‌شود.
            </p>
            <Input label="نام شرکت (پیشوند نام کاربری) *" value={editForm.name} onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))} />
            <div style={{ marginTop: 12 }}>
              <Input label="نام نمایشی" placeholder="اختیاری؛ اگر خالی باشد نام شرکت نمایش داده می‌شود" value={editForm.display_name} onChange={(e) => setEditForm((f) => ({ ...f, display_name: e.target.value }))} />
            </div>
            <div style={{ marginTop: 12 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#334155', display: 'block', marginBottom: 6 }}>توضیحات</label>
              <textarea
                value={editForm.description}
                onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
                rows={4}
                style={{ width: '100%', boxSizing: 'border-box', padding: '10px 14px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', resize: 'vertical' }}
              />
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <Btn variant="secondary" onClick={() => setEditTarget(null)} disabled={savingEdit}>انصراف</Btn>
              <Btn onClick={saveEdit} loading={savingEdit}>ذخیره</Btn>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
        }}
        >
          <div style={{
            background: '#fff', borderRadius: 14, padding: 24, width: '100%', maxWidth: 420,
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
          }}
          >
            <h3 style={{ fontSize: 15, fontWeight: 700, margin: '0 0 8px', color: '#0f172a' }}>
              حذف شرکت «{deleteTarget.name}»؟
            </h3>
            <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 16px', lineHeight: 1.7 }}>
              فقط وقتی امکان‌پذیر است که هیچ کاربری به این شرکت وصل نباشد. مکان‌ها و گیرندگان ماموریت مرتبط هم حذف می‌شوند.
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Btn variant="secondary" onClick={() => setDeleteTarget(null)} disabled={deleting}>انصراف</Btn>
              <Btn variant="danger" onClick={confirmDelete} loading={deleting}>حذف</Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
